<template>
  <div id="customEditor" style="min-height: 100vh">

    <v-navigation-drawer
      v-model="showDrawer"
      app
      min-width="56px"
      width="300px"
      right
      clipped
    >
      <v-navigation-drawer
        v-model="showDrawer"
        absolute
        color="grey lighten-3"
        mini-variant
      >
        <v-list>
          <v-list-item v-for="sidebarItem in sidebarItems" v-bind:key="sidebarItem.name">
            <v-icon @click="selectSidebarView(sidebarItem.component)">{{sidebarItem.icon}}</v-icon>
          </v-list-item>
        </v-list>
      </v-navigation-drawer>
      <component
        v-bind:is="selectedSidebarView"
        v-bind:html.sync="emailTemplateHtml"
        help-url="/help/CustomEditor.md"
        :template-key="templateKey"
      ></component>
    </v-navigation-drawer>

    <!-- TITLE/APP bar -->
    <v-app-bar color="primary" dark app clipped-right>
      <v-toolbar-title>Custom Email Builder</v-toolbar-title>
      <v-subheader>{{ emailTemplateName }}</v-subheader>
      <v-spacer />
      <v-menu bottom origin="center center" transition="scale-transition">
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            dark
            v-bind="attrs"
            v-on="on"
          >
            <v-icon>mdi-alpha-a-circle</v-icon>
            Actions
          </v-btn>
      </template>
      <v-list>
        <v-list-item @click="openTemplate"><v-list-item-title>Open Template</v-list-item-title></v-list-item>
        <v-list-item @click="htmlPreview"><v-list-item-title>Browser Preview</v-list-item-title></v-list-item>
        <v-list-item @click="sendEmailPreview"><v-list-item-title>Email Preview</v-list-item-title></v-list-item>
        <v-list-item @click="saveTemplate" v-if="!isStarterTemplate"><v-list-item-title>Save as Template</v-list-item-title></v-list-item>
        <v-list-item @click="$router.go(-1)"><v-list-item-title>Exit Without Save</v-list-item-title></v-list-item>
      </v-list>
      </v-menu>
      <v-btn class="ml-2" @click="save"><v-icon>mdi-content-save</v-icon>Save</v-btn>
      <input type="file" ref="templateUploadInput" hidden="true" @change="loadTemplate" />
    </v-app-bar>
    <v-main>
      <v-container fluid class="ma-0 pa-0">
        <v-responsive>
          <codemirror
            v-model="emailTemplateHtml"
            :options="editorOptions"
          >
          </codemirror>
        </v-responsive>
        <!-- DIALOGS -->

        <!--
          ON SAVE MODAL
        -->
        <v-dialog max-width="500px" v-model="showSaveDialog">
          <v-card>
            <v-card-title>
              <span class="text-h5">Save Email Template</span>
            </v-card-title>
            <v-card-text>
              <v-alert
                v-model="showModalError"
                dismissible
                type="error"
                transition="scale-transition"
              >{{ modalError }}</v-alert>
              <v-form @submit.prevent="saveEmailBuilderData">
                <v-text-field
                  v-model="emailTemplateName"
                  v-validate="'required'"
                  data-vv-name="emailTemplateName"
                  label="Email Template Name"
                  required
                />
                <v-switch
                  v-model="emailTemplateOnlyUseR4MergeTags"
                  label="Use R4 Merge Tags Only"
                ></v-switch>
                <dealer-select
                  v-show="!dealerScopedView"
                  v-model="dealer.id"
                  v-validate="'required'"
                  data-vv-name="dealer_id"
                  data-vv-as="Dealer"
                  :error-messages="errors.collect('dealer_id')"
                  required
                />
                <v-switch
                  v-model="isGlobalTemplate"
                  label="Global Template"
                ></v-switch>
                <p v-if="dealerScopedView && emailTemplate.global_template">Global Template Available to all Dealers</p>
                <p v-else>{{ dealer.name }}</p>
              </v-form>
            </v-card-text>
            <v-card-actions>
              <v-btn color="primary" @click.prevent="saveEmailBuilderData" type="submit">Save</v-btn>
              <v-btn @click.prevent="closeSaveDialog">Cancel</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <!--
          ON SAVE TEMPLATE MODAL
        -->
        <v-dialog max-width="500px" v-model="showTemplateSaveDialog">
          <v-card>
            <v-card-title>
              <span class="text-h5">Save as Starter Template</span>
            </v-card-title>
            <v-card-text>
              <v-alert
                v-model="showModalError"
                dismissible
                type="error"
                transition="scale-transition"
              >{{ modalError }}</v-alert>
              <v-form @submit.prevent="saveEmailTemplateData">
                <v-text-field
                  v-model="emailTemplateTemplateName"
                  label="Starter Template Name"
                  required
                />
                <v-switch
                  v-model="emailTemplateOnlyUseR4MergeTags"
                  label="Use R4 Merge Tags Only"
                ></v-switch>
              </v-form>
            </v-card-text>
            <v-card-actions>
              <v-btn color="primary" @click.prevent="saveEmailTemplateData">Save</v-btn>
              <v-btn @click.prevent="closeTemplateSaveDialog">Cancel</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <!--
          ON SEND PREVIEW EMAIL
        -->
        <v-dialog max-width="600px" v-model="showSendPreviewDialog">
          <send-email-preview-dialog
            :dealer-id="dealer.id"
            v-on:close="handleEmailPreviewClose"
            v-on:send="handleEmailPreviewSend"
            v-on:error="handleEmailPreviewError"
            v-on:selected-footer="previewFooterId = $event"
          >
            <template v-slot:header>
              <v-alert
                v-model="showModalError"
                dismissible
                type="error"
                transition="scale-transition"
              >{{ modalError }}</v-alert>
            </template>
          </send-email-preview-dialog>
        </v-dialog>

        <template>
          <div class="text-center">
            <v-overlay :value="isSendingEmailPreview">
              <p>Sending Email Preview</p>
              <v-progress-circular indeterminate size="64"></v-progress-circular>
            </v-overlay>
          </div>
        </template>
        <v-snackbar v-model="showNotice" top>
          {{ noticeMessage }}
          <v-btn color="pink" vertical text @click="doCloseNotice">Close</v-btn>
        </v-snackbar>

        <!-- BROWSER PREVIEW DIALOG -->
        <preview-template-dialog
          v-model="showPreviewDialog"
          :use-live-view-api="true"
          :html-template="previewHtmlTemplate"
          :email-footer-id="previewFooterId"
        >
        </preview-template-dialog>

        <!-- ALERTS -->
        <v-alert
          v-if="appNotification.type !== 'mini'"
          v-model="appNotification.visible"
          :dismissible="appNotification.showClose"
          :type="appNotification.type"
          transition="scale-transition"
        >
          {{ appNotification.message }}
        </v-alert>
        <v-snackbar
          v-if="appNotification.type === 'mini'"
          v-model="appNotification.visible"
          transition="scale-transition"
          top
        >
          {{ appNotification.message }}
          <v-btn
            v-if="appNotification.showClose"
            text
            color="pink"
            @click="appNotification.visible = false"
          >
            Close
          </v-btn>
        </v-snackbar>
      </v-container>
    </v-main>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import MergeTagSidebar from './MergeTagSidebar'
import SpecialLinkSidebar from './SpecialLinkSidebar'
import HelpSidebar from './HelpSidebar'
import ImageSidebar from './ImageSidebar'
import codemirror from '@/components/shared/codemirror'
import EmailTemplateCommonMixin from './EmailTemplateCommonMixin'
import { DealerSelect, SendEmailPreviewDialog } from '@/components/inputs'
import { EmailTemplateApi } from '@/services'
import { isNil, path } from 'ramda'
import PreviewTemplateDialog from '@/components/email_builder/PreviewTemplateDialog'

const { v4: uuid } = require('uuid')

const CodeMirror = require('codemirror/lib/codemirror.js')

export default {
  name: 'custom-builder',
  templateType: 'CustomEmailTemplate',
  mixins: [EmailTemplateCommonMixin],
  props: {
    emailTemplateStarter: {
      type: [String],
      required: false,
      default: 'blank-custom-template'
    }
  },
  components: {
    [MergeTagSidebar.name]: MergeTagSidebar,
    [SpecialLinkSidebar.name]: SpecialLinkSidebar,
    [ImageSidebar.name]: ImageSidebar,
    [HelpSidebar.name]: HelpSidebar,
    [codemirror.name]: codemirror,
    [DealerSelect.name]: DealerSelect,
    [SendEmailPreviewDialog.name]: SendEmailPreviewDialog,
    [PreviewTemplateDialog.name]: PreviewTemplateDialog
  },
  data: function () {
    return {
      showDrawer: true,
      showSubDrawer: false,
      selectedSidebarView: MergeTagSidebar.name,
      requireTemplateHtml: true, // important override from common
      editorOptions: {
        mode: {
          name: 'htmlmixed',
          scriptTypes: [{ matches: /\/x-handlebars-template|\/x-mustache/i, mode: null }]
        },
        lineNumbers: true,
        lineWrapping: false,
        theme: 'solarized light',
        extraKeys: { 'Ctrl-Space': 'autocomplete' },
        hintOptions: { hint: this.mergeTagHints },
        viewportMargin: Infinity
      },
      sidebarItems: [
        { icon: 'mdi-tag-multiple', text: 'Merge Tags', component: MergeTagSidebar.name },
        { icon: 'mdi-link-variant', text: 'Special Links', component: SpecialLinkSidebar.name },
        { icon: 'mdi-image', text: 'Images', component: ImageSidebar.name },
        { icon: 'mdi-help', text: 'Help', component: HelpSidebar.name }
      ],
      showPreviewDialog: false,
      previewHtmlTemplate: null,
      previewFooterId: null
    }
  },
  computed: {
    ...mapState('ApplicationStore', {
      appNotification: 'appNotification',
      AccountAdminGroup: state => state.groups.ACCOUNT_ADMIN,
      dealerScopedView: 'dealerScopedView'
    }),
    ...mapState('DealerStore', ['dealer']),
    ...mapState('EmailTemplateStore', ['mergeTags', 'specialLinks']),
    allTags () {
      return this.mergeTags.map(mt => mt.value).concat(
        ...this.specialLinks.map(sl => sl.link)
      )
    },
    templateKey () {
      return this.emailTemplateJson.template_key
    }
  },
  methods: {
    openTemplate () {
      this.$refs['templateUploadInput'].click()
    },
    loadTemplate () {
      const file = this.$refs['templateUploadInput'].files[0]
      const reader = new FileReader()
      reader.onload = function (fileLoadedEvent) {
        this.emailTemplateHtml = fileLoadedEvent.target.result
        this.$refs['templateUploadInput'].value = '' // reset input to fix bug where @change event does not work 2nd time
      }.bind(this)
      reader.readAsText(file)
    },
    copyMergeTags (mTags) {
      this.mergeTags = mTags
    },
    copySpecialLinks (sLinks) {
      this.specialLinks = sLinks
    },
    mergeTagHints (cm, option) {
      const that = this
      return new Promise(function (resolve, reject) {
        setTimeout(function () {
          let cursor = cm.getCursor()
          let line = cm.getLine(cursor.line)
          let start = cursor.ch
          let end = cursor.ch
          while (start && /\w/.test(line.charAt(start - 1))) { --start }
          while (end < line.length && /\w/.test(line.charAt(end))) { ++end }

          const hits = []
          let word = line.slice(start, end).toLowerCase()
          for (let i = 0; i < that.allTags.length; i++) {
            if (that.allTags[i].includes(word)) {
              hits.push(that.allTags[i])
            }
          }
          return resolve({ list: hits,
            from: CodeMirror.Pos(cursor.line, start),
            to: CodeMirror.Pos(cursor.line, end) })
        })
      })
    },
    save: function () {
      this.resetModalError()
      this.showSaveDialog = true
    },
    saveTemplate: function () {
      this.resetModalError()
      this.emailTemplateTemplateName = '' // reset name from last save
      this.showTemplateSaveDialog = true
    },
    sendEmailPreview: function () {
      this.resetModalError()
      this.showSendPreviewDialog = true
      this.emailPreviewHtml = this.emailTemplateHtml
    },
    htmlPreview: function () {
      this.previewHtmlTemplate = this.emailTemplateHtml
      this.showPreviewDialog = true
    },
    loadEditor: function () {
      const templatePromise = !isNil(this.emailTemplateId)
        ? EmailTemplateApi.getEmailTemplate(this.emailTemplateId)
        : EmailTemplateApi.getStarterTemplate(this.emailTemplateStarter, this.emailTemplateType)

      templatePromise
        .then(resp => {
          this.emailTemplate = this.configureTemplateKey(resp.data)
        })
        .catch(error => {
          this.$logger.error(error.message)
          this.$emit('load-failure', error)
        })
    },
    selectSidebarView: function (componentName) {
      this.selectedSidebarView = componentName
    },
    configureTemplateKey: function (emailTemplate) {
      const tkey = path(['template_json', 'template_key'], emailTemplate)
      if (tkey) {
        return emailTemplate
      } else {
        emailTemplate.template_json.template_key = uuid()
        return emailTemplate
      }
    }
  },
  mounted: function () {
    this.$store.dispatch('EmailTemplateStore/getMergeTags')
    this.$store.dispatch('EmailTemplateStore/getSpecialLinks')
    this.loadEditor()
  }
}
</script>

<style>
.CodeMirror {
  min-height: 92vh;
  max-height: 92vh;
}
</style>
